import React, { useEffect, useState } from 'react'
import { TrendingUp, TrendingDown, Clock, RefreshCw } from 'lucide-react'
import { useCurrencyStore } from '@/stores/currencyStore'
import { formatTimeAgo, formatPercentage } from '@/utils/formatters'

interface RateDisplayProps {
  fromCurrency: string
  toCurrency: string
}

const RateDisplay: React.FC<RateDisplayProps> = ({
  fromCurrency,
  toCurrency
}) => {
  const { getExchangeRate, exchangeRates, isLoading } = useCurrencyStore()
  const [currentRate, setCurrentRate] = useState<number | null>(null)
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const pairKey = `${fromCurrency}-${toCurrency}`
  const cachedRate = exchangeRates.get(pairKey)

  // Fetch rate when currency pair changes
  useEffect(() => {
    const fetchRate = async () => {
      setIsRefreshing(true)
      try {
        const rate = await getExchangeRate(fromCurrency, toCurrency)
        if (rate) {
          setCurrentRate(rate.rate)
          setLastUpdate(new Date(rate.timestamp))
        }
      } catch (error) {
        console.error('Failed to fetch rate:', error)
      } finally {
        setIsRefreshing(false)
      }
    }

    if (fromCurrency && toCurrency && fromCurrency !== toCurrency) {
      fetchRate()
    }
  }, [fromCurrency, toCurrency, getExchangeRate])

  // Update from cached data
  useEffect(() => {
    if (cachedRate) {
      setCurrentRate(cachedRate.rate)
      setLastUpdate(new Date(cachedRate.timestamp))
    }
  }, [cachedRate])



  if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) {
    return null
  }

  return (
    <div className="card p-3">
      <div className="mb-2">
        <h3 className="text-base font-semibold text-gray-900">
          Exchange Rate
        </h3>
      </div>

      {currentRate ? (
        <div className="space-y-2">
          {/* Main Rate Display */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-gray-600">
                1 {fromCurrency} =
              </span>
              <span className="text-xl font-bold text-gray-900 font-mono">
                {currentRate.toFixed(4)}
              </span>
              <span className="text-gray-600">
                {toCurrency}
              </span>
            </div>
            
            {/* Mock rate change indicator */}
            <div className="flex items-center space-x-1 text-sm">
              {Math.random() > 0.5 ? (
                <>
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-green-600 font-medium">
                    +{formatPercentage(Math.random() * 2)}
                  </span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-4 h-4 text-red-600" />
                  <span className="text-red-600 font-medium">
                    -{formatPercentage(Math.random() * 2)}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Inverse Rate */}
          <div className="text-sm text-gray-600">
            1 {toCurrency} = {(1 / currentRate).toFixed(4)} {fromCurrency}
          </div>

          {/* Last Update */}
          {lastUpdate && (
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>Updated {formatTimeAgo(lastUpdate)}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center py-4">
          {isRefreshing || isLoading ? (
            <div className="flex items-center space-x-2 text-gray-500">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Loading rate...</span>
            </div>
          ) : (
            <span className="text-gray-500">Rate not available</span>
          )}
        </div>
      )}
    </div>
  )
}

export default RateDisplay